import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { prefersReducedMotion } from "@/utils/motion";

// 200 km/h in meters per second, for the distance-behind-real-time readouts
const SPEED_200_KMH = 55.6;

const DEVICE_BASE = 0.25;
const DEVICE_JITTER = 0.08;
const APP_BASE = 0.6;
const APP_JITTER = 0.2;

interface LiveGap {
  gap: string;
  distance: string;
}

const STATIC_GAPS: Record<"device" | "app", LiveGap> = {
  device: { gap: "+0.2–0.3 s", distance: "11–17 m" },
  app: { gap: "+0.5–0.7 s", distance: "28–39 m" },
};

function jitteredGap(base: number, jitter: number): LiveGap {
  const seconds = base + (Math.random() - 0.5) * jitter;
  return {
    gap: `+${seconds.toFixed(2)} s`,
    distance: `${Math.round(seconds * SPEED_200_KMH)} m`,
  };
}

export default function LatencyBoard() {
  const { t } = useTranslation("pages");
  const reduced = prefersReducedMotion();
  const [device, setDevice] = useState<LiveGap>(STATIC_GAPS.device);
  const [app, setApp] = useState<LiveGap>(STATIC_GAPS.app);

  useEffect(() => {
    if (reduced) return;
    const timer = setInterval(() => {
      setDevice(jitteredGap(DEVICE_BASE, DEVICE_JITTER));
      setApp(jitteredGap(APP_BASE, APP_JITTER));
    }, 1000);
    return () => clearInterval(timer);
  }, [reduced]);

  const rows = [
    {
      key: "device",
      pos: "P1",
      accent: "violet" as const,
      fastest: true,
      live: device,
    },
    {
      key: "app",
      pos: "P2",
      accent: "blue" as const,
      fastest: false,
      live: app,
    },
    {
      key: "platform",
      pos: "P3",
      accent: "gray" as const,
      fastest: false,
      live: { gap: "+2–5 s", distance: "110–280 m" },
    },
  ];

  return (
    <section aria-label={t("trackcast.board.sectionTitle")}>
      <p className="section-title">{t("trackcast.board.sectionTitle")}</p>
      <h3 className="text-xl font-bold tracking-tight mb-5">
        {t("trackcast.board.lead")}
      </h3>
      <div className="bg-white border border-secondary-200 rounded-xl overflow-hidden">
        <div className="hidden sm:grid grid-cols-[56px_1.1fr_1fr_180px] gap-4 items-center px-5 py-3 bg-secondary-50 border-b border-secondary-200 font-mono text-[10.5px] tracking-[0.15em] uppercase text-secondary-500">
          <span>{t("trackcast.board.colPos")}</span>
          <span>{t("trackcast.board.colEntry")}</span>
          <span>{t("trackcast.board.colSetup")}</span>
          <span className="text-right">{t("trackcast.board.colGap")}</span>
        </div>
        {rows.map((row) => (
          <div
            key={row.key}
            className={clsx(
              "grid grid-cols-[44px_1fr_auto] sm:grid-cols-[56px_1.1fr_1fr_180px] gap-4 items-center px-5 py-5 border-b border-secondary-100 last:border-b-0",
              row.accent === "violet" && "shadow-[inset_4px_0_0_#7C3AED]",
              row.accent === "blue" && "shadow-[inset_4px_0_0_#0066FF]",
              row.accent === "gray" && "shadow-[inset_4px_0_0_#E8E8E8]",
            )}
          >
            <span
              className={clsx(
                "font-mono font-bold",
                row.accent === "violet" && "text-violet-600",
                row.accent === "blue" && "text-primary-600",
                row.accent === "gray" && "text-secondary-400",
              )}
            >
              {row.pos}
            </span>
            <span
              className={clsx(
                "font-semibold",
                row.accent === "gray" && "text-secondary-400",
              )}
            >
              {t(`trackcast.board.${row.key}Entry`)}
              {row.fastest && (
                <span className="ml-2 align-[2px] rounded bg-violet-600 px-1.5 py-0.5 font-mono text-[9px] font-bold tracking-[0.14em] text-white">
                  {t("trackcast.board.fastest")}
                </span>
              )}
              <small className="block font-normal text-xs text-secondary-500">
                {t(`trackcast.board.${row.key}Sub`)}
              </small>
            </span>
            <span className="hidden sm:block text-sm text-secondary-500">
              {t(`trackcast.board.${row.key}Setup`)}
            </span>
            <span
              className={clsx(
                "text-right font-mono font-bold tabular-nums whitespace-nowrap",
                row.accent === "violet" && "text-violet-600",
                row.accent === "blue" && "text-primary-600",
                row.accent === "gray" && "text-secondary-400 text-sm",
              )}
            >
              {row.live.gap}
              <small className="block font-normal text-[10px] tracking-[0.04em] text-secondary-400">
                {t("trackcast.board.distanceAt200", {
                  distance: row.live.distance,
                })}
              </small>
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3 font-mono text-[11px] tracking-[0.06em] text-secondary-500">
        <b className="text-violet-600">{t("trackcast.board.noteLead")}</b>{" "}
        {t("trackcast.board.noteBody")}
      </p>
    </section>
  );
}
